import React, { createContext, useContext, useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
    id: string;
    message: string;
    type: ToastType;
    duration?: number;
}

interface ToastContextType {
    showToast: (message: string, type: ToastType, duration?: number) => void;
    showConfirm: (title: string, message: string, isDestructive?: boolean) => Promise<boolean>;
    removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const [confirmState, setConfirmState] = useState<{
        resolve: (val: boolean) => void;
        title: string;
        message: string;
        isDestructive: boolean;
    } | null>(null);

    const showToast = (message: string, type: ToastType, duration = 3000) => {
        const id = Math.random().toString(36).substring(2, 9);
        setToasts(prev => [...prev, { id, message, type, duration }]);
    };

    const showConfirm = (title: string, message: string, isDestructive = false): Promise<boolean> => {
        return new Promise((resolve) => {
            setConfirmState({ resolve, title, message, isDestructive });
        });
    };

    const handleConfirm = (value: boolean) => {
        if (confirmState) {
            confirmState.resolve(value);
            setConfirmState(null);
        }
    };

    const removeToast = (id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    return (
        <ToastContext.Provider value={{ showToast, showConfirm, removeToast }}>
            {children}

            {/* Confirmation Dialog matching Toast Style */}
            {confirmState && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-lg shadow-2xl max-w-sm w-full animate-in zoom-in-95 duration-200">
                        <div className="flex items-start gap-4 mb-6">
                            <div className="p-3 rounded-full shrink-0 bg-red-500/10 text-red-500 shadow-[0_0_15px_rgba(239,68,68,0.1)] border border-red-500/20">
                                <AlertTriangle size={24} />
                            </div>
                            <div>
                                <h3 className="text-lg font-display font-bold text-white mb-1 uppercase italic tracking-tight">{confirmState.title}</h3>
                                <p className="text-zinc-400 text-sm leading-relaxed">{confirmState.message}</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => handleConfirm(false)}
                                className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded font-bold text-[10px] uppercase transition-colors border border-zinc-700"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleConfirm(true)}
                                className={`flex-[2] px-4 py-2 rounded font-bold text-[10px] uppercase transition-all shadow-lg text-white border border-red-500/50 
                                    ${confirmState.isDestructive
                                        ? 'bg-red-600 hover:bg-red-500 shadow-red-600/40'
                                        : 'bg-red-700 hover:bg-red-600 shadow-red-700/30'
                                    }`}
                            >
                                {confirmState.isDestructive ? 'ELIMINAR DEFINITIVAMENTE' : 'CONFIRMAR ACCIÓN'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
                {toasts.map(toast => (
                    <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
                ))}
            </div>
        </ToastContext.Provider>
    );
};

const ToastItem: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => {
    useEffect(() => {
        if (toast.duration) {
            const timer = setTimeout(() => {
                onClose();
            }, toast.duration);
            return () => clearTimeout(timer);
        }
    }, [toast, onClose]);

    const getIcon = () => {
        switch (toast.type) {
            case 'success': return <CheckCircle size={20} className="text-[#25D366]" />;
            case 'error': return <AlertCircle size={20} className="text-red-500" />;
            case 'warning': return <AlertTriangle size={20} className="text-yellow-500" />;
            case 'info': return <Info size={20} className="text-blue-500" />;
        }
    };

    const getBorderColor = () => {
        switch (toast.type) {
            case 'success': return 'border-[#25D366]';
            case 'error': return 'border-red-500';
            case 'warning': return 'border-yellow-500';
            case 'info': return 'border-blue-500';
        }
    };

    return (
        <div className={`
            flex items-center gap-3 p-4 bg-black border ${getBorderColor()} rounded shadow-lg
            transform transition-all duration-300 ease-in-out animate-in slide-in-from-right fade-in
            min-w-[300px] max-w-md
        `}>
            {getIcon()}
            <p className="text-sm font-medium text-white flex-1">{toast.message}</p>
            <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
                <X size={16} />
            </button>
        </div>
    );
};
